import clsx from 'clsx'
import compact from 'lodash/compact'
import isEmpty from 'lodash/isEmpty'
import map from 'lodash/map'
import noop from 'lodash/noop'
import { useRouter } from 'next/router'
import React, { useCallback, useMemo, useState } from 'react'

import { ampli } from '@Analytics/ampli'
import SvgCheck from '@Assets/SVG/Check'
import SvgMinus from '@Assets/SVG/Minus'
import SvgPlus from '@Assets/SVG/Plus'
import { Chip } from '@Components/Chips/Chip/Chip'
import { Milestones } from '@CoreComponents/Progressbars/Milestones'
import { CartV2LineItem } from '@Domains/Cart/Feature/CartV2/CartV2'
import { useAddToCart } from '@Domains/Cart/Hooks/useAddToCart'
import { ProductItem } from '@Domains/Product/Components/ProductItem'
import { AddToCartPromptSuccessTacticFragmentFragment, useCartQuery, useRemoveFromCartMutation, useSetQuantityInCartMutation } from '@Generated/graphql'
import { useSplitTest } from '@Globals/SplitTests/Hooks'
import { useTranslation } from '@Globals/Translations'
import { Spinner } from '@Shared/Components/Spinner/Spinner'

import styles from './QuantityDiscount.module.scss'

const CartMutationOptions = {
  sourceID: 'quantityDiscount',
  context: {
    domainProxy: true,
    userSpecific: true,
  },
}

type QuantityProduct = {
  asset: string
  external?: boolean
  id: string
  name: string
  quantity: number
  sizeLabel?: string | null
  variantID?: string | null
}

type Tactic = Extract<AddToCartPromptSuccessTacticFragmentFragment, { __typename: 'TacticRelativeDiscount' }>
type ProductToLineItem = Record<string, { lineItemID: string, quantity: number }>

const getLineItemVariantSize = (lineItem: CartV2LineItem) => (lineItem.variant?.size?.__typename === 'BaseAttribute' ? lineItem.variant?.size : undefined)
// @ts-ignore
const getSizeLabel = (item: CartV2LineItem, locale: string) => getLineItemVariantSize(item)?.value?.label[locale] || getLineItemVariantSize(item)?.value?.key

const LineItemToQuantityProduct = (lineItem: CartV2LineItem, locale: string, external?: boolean): QuantityProduct => ({
  name: lineItem.product?.name || '',
  id: lineItem.product?.id || '',
  asset: lineItem.product?.primaryAsset?.url || '',
  variantID: lineItem.variant?.id,
  quantity: lineItem.quantity,
  external,
  sizeLabel: getSizeLabel(lineItem, locale),
})
const TacTicQuantityProduct = (index: number, lineItem: CartV2LineItem, locale: string, tactic: Tactic): QuantityProduct => {
  const products = tactic.products || []
  const productIDs = tactic.productIDs || []
  const relativeDiscounts = tactic.relativeDiscounts || []
  return ({
    id: products[index]?.id || '',
    name: products[index]?.name || '',
    asset: (products[index] as any)?.primaryAsset?.url || '',
    variantID: productIDs[index],
    quantity: relativeDiscounts[index]?.quantity,
    external: false,
    sizeLabel: getSizeLabel(lineItem, locale),
  })
}
const TacTicQuantityProducts = (lineItem: CartV2LineItem, locale: string, tactic?: Tactic) => compact(
  tactic?.products?.map((_, index) => TacTicQuantityProduct(index, lineItem, locale, tactic)),
)

export const QuantityDiscount = ({
  lineItem,
  showProgressBar,
  tactic,
}: {
  lineItem: CartV2LineItem
  showProgressBar?: boolean
  tactic?: Tactic
}) => {
  const { locale = 'en-GB' } = useRouter()
  const campaignOverrideV1 = useSplitTest('campaign_override_v1', 'b')
  const { t, tDanger } = useTranslation()
  const [interactedItems, setInteractedItems] = useState<Record<string, boolean>>({})
  const { data } = useCartQuery({ ...CartMutationOptions, fetchPolicy: 'cache-first' })
  const [addToCart] = useAddToCart({ ...CartMutationOptions, withoutSuccessPrompt: true, useCache: false })
  const [removeFromCart] = useRemoveFromCartMutation(CartMutationOptions)
  const [setQuantityInCart] = useSetQuantityInCartMutation(CartMutationOptions)
  const lineItems = useMemo(() => data?.cart?.lineItems || [], [data?.cart?.lineItems])

  const products: QuantityProduct[] = useMemo(
    () => {
      const result = [LineItemToQuantityProduct(lineItem, locale), ...TacTicQuantityProducts(lineItem, locale, tactic)]
      let lineItemsWithDifferentVariant = lineItems.filter(li => result.find(p => (p.id === li.product?.id && p.variantID !== li.variant?.id)))
      lineItemsWithDifferentVariant = compact(lineItemsWithDifferentVariant)
      const lineItemsWithOtherVariants = lineItemsWithDifferentVariant.map(li => LineItemToQuantityProduct(li, locale, true))
      return [...result, ...lineItemsWithOtherVariants]
    },
    [lineItem, lineItems, locale, tactic],
  )

  const relevantLineItemMap = useMemo(
    () => products.reduce<ProductToLineItem>((acc, curr) => {
      const foundLineItem = lineItems?.find(li => li.variant?.id === curr.variantID && li.product?.id === curr.id)
      if (foundLineItem && foundLineItem.id && foundLineItem?.quantity) {
        acc[`${curr.id}_${curr.variantID}`] = {
          lineItemID: foundLineItem.id!,
          quantity: foundLineItem.quantity!,
        }
      }
      return acc
    }, {}),
    [lineItems, products],
  )

  const totalInCart = useMemo(() => Object.values(relevantLineItemMap).reduce((acc, curr) => acc + curr.quantity, 0), [relevantLineItemMap])
  const relativeDiscounts = useMemo(() => tactic?.relativeDiscounts || [], [tactic])
  const relevantDiscountIndex: number = useMemo(
    () => relativeDiscounts.findIndex((a, i) => a.quantity > totalInCart || i === relativeDiscounts.length - 1),
    [relativeDiscounts, totalInCart],
  )

  const updateInteraction = useCallback((variantID: string, state: boolean) => {
    setInteractedItems(prev => ({ ...prev, [variantID]: state }))
  }, [])

  return (
    <div className={clsx('fullwidth', styles.wrapper, campaignOverrideV1 && styles.campaignOverride)}>
      <div className={clsx(styles.header, showProgressBar && styles.showProgressBar)}>
        {campaignOverrideV1 && <Chip color="valentine_b_small" />}
        {showProgressBar
          ? (
            <>
              <div className={styles.progressHeader}>{t('cart:BUNDLE_DISCOUNT_LABEL')}</div>
              <Milestones
                inverted
                milestones={(tactic?.relativeDiscounts || []).map((rd, i) => {
                  const isRelevantDiscountIndex = relevantDiscountIndex === i
                  const percentage = Math.min((100 / rd.quantity) * totalInCart, 100)
                  const defaultPercentage = i > relevantDiscountIndex ? 0 : 100
                  const progress = isRelevantDiscountIndex ? percentage : defaultPercentage

                  return ({
                    disabled: true,
                    icon: <SvgCheck />,
                    id: `QuantityDiscount_${rd.quantity}_${rd.percentage}`,
                    onClick: noop,
                    progress,
                    text: `${t('products:SAVE_X_VALUE', { arg1: rd.percentage })}%`,
                  })
                })}
              />
            </>
          )
          : (
            <div className={styles.addMore}>
              {relativeDiscounts[relevantDiscountIndex]?.quantity > totalInCart
                ? (
                  <>
                    {t('products:ADD_COUNT_MORE', { count: (relativeDiscounts[relevantDiscountIndex]?.quantity || 0) - totalInCart })}
                    <span
                      {...tDanger('products:GET_X_OFF', {
                        percentage: relativeDiscounts[relevantDiscountIndex]?.percentage,
                      })}
                    />
                  </>

                )
                : (
                  <span
                    {...tDanger('products:YOU_HAVE_EARNED_X_OFF', {
                      percentage: relativeDiscounts[relevantDiscountIndex]?.percentage,
                    })}
                  />
                )}
            </div>
          )}
      </div>

      <div className={styles.list}>
        {showProgressBar && (
          <div className={styles.addMore}>
            {relativeDiscounts[relevantDiscountIndex]?.quantity > totalInCart
              ? (
                <>
                  {t('products:ADD_COUNT_MORE', { count: (relativeDiscounts[relevantDiscountIndex]?.quantity || 0) - totalInCart })}
                </>

              )
              : (
                <span
                  {...tDanger('products:YOU_HAVE_EARNED_X_OFF', {
                    percentage: relativeDiscounts[relevantDiscountIndex]?.percentage,
                  })}
                />
              )}
          </div>
        )}
        {map(products, (product, index) => {
          const productID = product.id
          const variantID = product.variantID || ''
          const relativeLineItem = relevantLineItemMap[`${productID}_${variantID}`]
          const quantity = relativeLineItem?.quantity || 0
          const active = lineItem.product?.id === productID && variantID === lineItem.variant?.id
          const loading = interactedItems[variantID] || false

          const onPlus = () => {
            updateInteraction(variantID, true)
            if (relativeLineItem) {
              setQuantityInCart({
                variables: {
                  lineItems: [
                    {
                      ...relativeLineItem,
                      quantity: relativeLineItem.quantity + 1,
                    },
                  ],
                },
              })
                .then(() => ampli.cartQtyChange({
                  is_quantity_increased: true,
                  was_buttons_used: true,
                  variant_id: variantID,
                  product_id: productID,
                }))
                .then(() => updateInteraction(variantID, false))
            } else {
              addToCart([
                {
                  quantity: 1,
                  productID,
                  variantID,
                },
              ])
                .then(() => updateInteraction(variantID, false))
            }
          }
          const onMinus = () => {
            updateInteraction(variantID, true)
            if (relativeLineItem.quantity - 1 < 1) {
              removeFromCart({ variables: { lineItemID: relativeLineItem.lineItemID } })
                .then(a => ampli.cartRemoveRes({
                  is_success: isEmpty(a.errors),
                  variant_id: variantID,
                  product_id: productID,
                }))
                .then(() => updateInteraction(variantID, false))
            } else {
              setQuantityInCart({
                variables: {
                  lineItems: [
                    {
                      ...relativeLineItem,
                      quantity: relativeLineItem.quantity - 1,
                    },
                  ],
                },
              })
              // eslint-disable-next-line max-len
                .then(() => ampli.cartQtyChange({
                  is_quantity_increased: false,
                  was_buttons_used: true,
                  variant_id: variantID,
                  product_id: productID,
                }))
                .then(() => updateInteraction(variantID, false))
            }
          }

          return (
            <ProductItem
              key={`QuantityDiscountItem_${productID}_${index}`}
              action={(
                <div className={styles.productItemActions}>
                  {!loading && quantity !== 0 && (
                    <button
                      aria-label={t('products:REMOVE_ITEM')}
                      className={styles.roundButton}
                      disabled={active && quantity === 1}
                      onClick={onMinus}
                      type="button"
                    >
                      <SvgMinus />
                    </button>
                  )}
                  {!loading && quantity !== 0 && quantity}
                  <button
                    aria-label={t('products:ADD_ITEM')}
                    className={styles.roundButton}
                    onClick={onPlus}
                    type="button"
                  >
                    {loading
                      ? (
                        <Spinner className={styles.spinner} />
                      )
                      : (
                        <SvgPlus />
                      )}
                  </button>
                </div>
              )}
              className={styles.productItem}
              content={(
                <>
                  {product.name}
                  {product.sizeLabel && (
                    <span className={styles.sizeLabel}>
                      {t('cms:SIZE')}
                      {': '}
                      <strong>{product.sizeLabel || ''}</strong>
                    </span>
                  )}
                </>
              )}
              imageUrl={product.asset}
            />
          )
        })}
      </div>
    </div>
  )
}

